/**
 * The two flags of one service on its Apps page: "Read-only" and "No build (context only)". The project's owner turns
 * them on or off; each change asks first, saying what it does (the Terminal's part in vfs-service's own words), and
 * goes to vfs-service (`PUT /repo-flags`), which records it in the project's Activity and enforces it everywhere.
 * Anyone else sees the state, with "Only the project owner can change this". A context repo's flags cannot be
 * turned off (vfs-service answers `context_fixed`, shown as it is).
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Switch } from 'antd';
import useRepoFlags from '../../hooks/useRepoFlags';
import { setRepoFlags } from '../../utils/vfsClient';
import './RepoFlags.css';

export const OWNER_ONLY_TIP = 'Only the project owner can change this';

/** The confirmation's title and sentences for turning `flag` (`readOnly` | `noBuild`) `on` or off in `repo`. */
export function flagChangeText(flag, on, repo, terminal = '') {
  if (flag === 'readOnly') {
    return on
      ? {
        title: `Make ${repo} read-only?`,
        lines: [
          `Nobody (people or AI agents) can change, commit or push files in ${repo}: edits in Code, the file tree, the agent's tools and Git are refused. Pull and fetch to refresh it stay allowed.`,
          terminal,
        ].filter(Boolean),
        ok: 'Make read-only',
      }
      : { title: `Let ${repo} be edited again?`, lines: [`People with edit access and AI agents can change ${repo} again.`], ok: 'Allow edits' };
  }
  return on
    ? {
      title: `Make ${repo} context only?`,
      lines: [`${repo} is never built or started: Start, Start all, Publish and quality runs skip it, and its running preview is stopped. It stays readable and searchable as context.`],
      ok: 'Never build it',
    }
    : {
      title: `Build ${repo} again?`,
      lines: [`${repo} can be built and started again: Start, Start all, Publish and quality runs include it.`],
      ok: 'Allow builds',
    };
}

const LABELS = { readOnly: 'Read-only', noBuild: 'No build (context only)' };

export default function RepoFlagControls({ projectId, repo }) {
  const { repos, canChange, terminal, loaded, publish } = useRepoFlags(projectId);
  const flags = repos[repo] || {};
  const [pending, setPending] = useState(null); // { flag, on }
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  if (!loaded) { return null; }

  const confirm = async() => {
    setBusy(true);
    setError(null);
    try {
      const answer = await setRepoFlags(projectId, repo, { [pending.flag]: pending.on });
      if (answer?.repos) {
        publish(answer.repos);
      } else if (answer?.flags) {
        publish({ ...repos, [repo]: answer.flags });
      }
      setPending(null);
    } catch (failure) {
      setError(failure?.message || 'The change could not be saved.');
    } finally {
      setBusy(false);
    }
  };

  const text = pending ? flagChangeText(pending.flag, pending.on, repo, terminal) : null;
  return (
    <div aria-label={`Flags of ${repo}`} className="repo-flag-controls" data-testid="repo-flag-controls" role="group">
      {['readOnly', 'noBuild'].map((flag) => (
        <label className={`repo-flag-toggle${canChange ? '' : ' is-locked'}`} key={flag} title={canChange ? undefined : OWNER_ONLY_TIP}>
          <Switch
            aria-label={LABELS[flag]}
            checked={Boolean(flags[flag])}
            data-testid={`repo-flag-${flag}`}
            disabled={!canChange || busy}
            onChange={(on) => { setError(null); setPending({ flag, on }); }}
            size="small"
          />
          <span>{LABELS[flag]}</span>
        </label>
      ))}
      {!pending && error && <span className="repo-flag-error" role="alert">{error}</span>}
      {pending && (
        <Modal
          cancelText="Cancel"
          className="repo-flag-confirm"
          confirmLoading={busy}
          okText={text.ok}
          onCancel={() => { if (!busy) { setPending(null); setError(null); } }}
          onOk={confirm}
          open
          title={text.title}
        >
          {text.lines.map((line) => <p key={line}>{line}</p>)}
          {error && <p className="repo-flag-error" role="alert">{error}</p>}
        </Modal>
      )}
    </div>
  );
}

RepoFlagControls.propTypes = {
  projectId: PropTypes.string.isRequired,
  // `services/<name>` or `context/<name>`.
  repo: PropTypes.string.isRequired,
};
